import ObjectField from '../ObjectField';
import ArrayField from '../ArrayField';
import AsyncSelect from '../AsyncSelect';
import AsyncAutocomplete from '../AsyncAutocomplete';
import Autocomplete from '../Autocomplete';
import Checkbox from '../Checkbox';
import CountrySelect from '../CountrySelect';
import DatePicker from '../DatePicker';
import DateTimeGroup from '../DateTimeGroup';
import DateTimePicker from '../DateTimePicker';
import OpeningHours from '../OpeningHours';
import Select from '../Select';
import Switch from '../Switch';
import TextField from '../TextField';
import ContactSearch from '../ContactSearch';
import WmsLocationSearch from '../WmsLocationSearch';
import ShippingMethodSearch from '../ShippingMethodSearch';
import VatCountrySelect from '../VatCountrySelect';
import RoutesSearch from '../RoutesSearch';
import sortArrayByProperty from '@afosto/utils/array/sortArrayByProperty';
import uuid from '@afosto/utils/uuid';
import { mixed, string, number, boolean, date, array, object } from 'yup';
import addMinLengthValidation from '@afosto/utils-react/validators/minLength';
import addMaxLengthValidation from '@afosto/utils-react/validators/maxLength';
import addMinNumberValidation from '@afosto/utils-react/validators/minNumber';
import addMaxNumberValidation from '@afosto/utils-react/validators/maxNumber';
import addMinItemsValidation from '@afosto/utils-react/validators/minItems';
import addMaxItemsValidation from '@afosto/utils-react/validators/maxItems';
import PaymentMethodSearch from '../PaymentMethodSearch';
import FulfilmentLocationSearch from '../FulfilmentLocationSearch';

addMinLengthValidation();
addMaxLengthValidation();
addMinNumberValidation();
addMaxNumberValidation();
addMinItemsValidation();
addMaxItemsValidation();

const INPUT_COMPONENTS = {
  TextField,
  Checkbox,
  DateTimeGroup,
  DateTimePicker,
  DatePicker,
  CountrySelect,
  AsyncSelect,
  AsyncAutocomplete,
  Select,
  Autocomplete,
  OpeningHours,
  ObjectField,
  ArrayField,
  ContactSearch,
  WmsLocationSearch,
  ShippingMethodSearch,
  PaymentMethodSearch,
  Switch,
  VatCountrySelect,
  RoutesSearch,
  FulfilmentLocationSearch,
};

const ARRAY_COMPONENTS = {
  object: 'ArrayField',
  default: 'Autocomplete',
  openingHours: 'OpeningHours',
  asyncAutocomplete: 'AsyncAutocomplete',
};

const validationTypes = {
  string,
  number,
  boolean,
  date,
  array,
  object,
};

const getSchemaType = (schema = {}) => {
  const { type } = schema;
  return type;
};

const isArrayOfObjects = schema =>
  schema?.type === 'array' && schema?.items?.type === 'object' && schema?.items?.properties;

const getArrayField = (schema, defaultProps) => {
  const { name } = defaultProps;
  const { uiOptions } = schema;
  const uiComponent = uiOptions?.component;
  const uiProps = uiOptions?.props ?? {};
  const isObjectArray = isArrayOfObjects(schema);
  let InputComponent = Autocomplete;
  let fields = [];

  if (uiComponent && Object.values(ARRAY_COMPONENTS).includes(uiComponent)) {
    InputComponent = INPUT_COMPONENTS[uiComponent];
  } else if (isObjectArray) {
    // eslint-disable-next-line no-use-before-define
    fields = parseSchema(schema.items, uiOptions, `${name}[0].`);
    InputComponent = INPUT_COMPONENTS[ARRAY_COMPONENTS.object];
  }

  return {
    type: 'array',
    name: schema?.name,
    InputComponent,
    inputProps: {
      ...defaultProps,
      ...uiProps,
      fields,
    },
    defaultValue: schema?.defaultValue ?? schema?.default ?? [],
    key: uuid(),
  };
};

const getObjectField = (schema, defaultProps) => {
  const { name } = defaultProps;
  const { uiOptions } = schema;
  const uiComponent = uiOptions?.component;
  const uiProps = uiOptions?.props ?? {};
  let InputComponent = ObjectField;

  if (uiComponent && INPUT_COMPONENTS[uiComponent]) {
    InputComponent = INPUT_COMPONENTS[uiComponent];
  }

  // eslint-disable-next-line no-use-before-define
  const fields = parseSchema(schema, uiOptions, `${name}.`);

  return {
    type: 'object',
    name: schema?.name,
    InputComponent,
    inputProps: {
      ...defaultProps,
      ...uiProps,
      fields,
    },
    defaultValue: schema?.defaultValue ?? schema?.default ?? {},
    key: uuid(),
  };
};

const getBooleanField = (schema, defaultProps) => {
  const { uiOptions } = schema;
  const uiComponent = uiOptions?.component;
  const uiProps = uiOptions?.props ?? {};
  let InputComponent = Switch;

  if (uiComponent && INPUT_COMPONENTS[uiComponent]) {
    InputComponent = INPUT_COMPONENTS[uiComponent];
  }

  return {
    type: 'boolean',
    name: schema?.name,
    InputComponent,
    inputProps: {
      ...defaultProps,
      ...uiProps,
    },
    defaultValue: schema?.defaultValue ?? schema?.default ?? false,
    key: uuid(),
  };
};

const getField = schema => {
  const type = getSchemaType(schema);
  const name = schema?.name;
  const { uiOptions } = schema;
  const uiComponent = uiOptions?.component;
  const uiProps = uiOptions?.props ?? {};
  let defaultProps = {
    name: schema?.fieldName,
    label: schema?.description ?? schema?.title ?? name,
  };

  if (type === 'array') {
    return getArrayField(schema, defaultProps);
  }

  if (type === 'object') {
    return getObjectField(schema, defaultProps);
  }

  if (type === 'boolean') {
    return getBooleanField(schema, defaultProps);
  }

  if (type === 'string' && Array.isArray(schema.enum)) {
    return {
      type,
      name,
      InputComponent: Select,
      inputProps: {
        ...defaultProps,
        ...uiProps,
        suggestions: sortArrayByProperty(
          schema.enum.map(value => ({ label: value, value })),
          'label',
        ),
      },
      defaultValue: schema?.defaultValue ?? schema?.default ?? '',
      key: uuid(),
    };
  }

  let InputComponent = TextField;

  if (uiComponent && INPUT_COMPONENTS[uiComponent]) {
    InputComponent = INPUT_COMPONENTS[uiComponent];
  }

  if (type === 'number' || type === 'integer') {
    let inputProps = { ...(defaultProps?.inputProps ?? {}) };

    if (schema?.minimum !== undefined) {
      inputProps = { ...inputProps, min: parseInt(schema.minimum, 10) };
    }

    if (schema?.maximum !== undefined) {
      inputProps = { ...inputProps, max: parseInt(schema.maximum, 10) };
    }

    defaultProps = { ...defaultProps, type: 'number', inputProps };
  }

  return {
    type,
    name,
    InputComponent,
    inputProps: {
      ...defaultProps,
      ...uiProps,
    },
    defaultValue: schema?.defaultValue ?? schema?.default ?? '',
    key: uuid(),
  };
};

export const parseSchema = (schema, uiSchema, path = '') =>
  Object.keys(schema?.properties ?? {}).reduce((acc, name) => {
    const field = schema.properties[name] || {};
    const uiOptions = uiSchema?.[name] ?? {};
    const fieldSchema = {
      ...field,
      id: uuid(),
      name,
      fieldName: `${path}${name}`,
      uiOptions,
    };

    return [...acc, getField(fieldSchema)];
  }, []);

export const getSchemaDefaultValues = fields =>
  fields.reduce((acc, field) => ({ ...acc, [field.name]: field.defaultValue }), {});

export const getValidationsFromSchema = schema => {
  const validations = Object.keys(schema?.properties ?? {}).reduce((acc, propertyKey) => {
    const isRequired = (schema?.required ?? []).includes(propertyKey);
    const property = schema.properties[propertyKey];
    let validationType = property?.type;

    if (validationType === 'integer') {
      validationType = 'number';
    }

    const validationFunc = validationTypes[validationType] ?? mixed;
    let validationRule = validationFunc();

    if (validationType === 'number') {
      validationRule = validationRule.transform(value => (Number.isNaN(value) ? undefined : value));

      if (property?.minimum !== undefined && property?.minimum !== '') {
        validationRule = validationRule.minNumber(parseInt(property?.minimum, 10));
      }

      if (property?.maximum !== undefined && property?.maximum !== '') {
        validationRule = validationRule.maxNumber(parseInt(property?.maximum, 10));
      }

      if (property.type === 'integer') {
        validationRule = validationRule.integer();
      }
    }

    if (validationType === 'string') {
      if (property?.minLength !== undefined && property?.minLength !== '') {
        validationRule = validationRule.minLength(parseInt(property?.minLength, 10));
      }

      if (property?.maxLength !== undefined && property?.maxLength !== '') {
        validationRule = validationRule.maxLength(parseInt(property?.maxLength, 10));
      }

      if (property?.pattern !== undefined && property?.pattern !== '') {
        validationRule = validationRule.matches(property?.pattern, {
          excludeEmptyString: !isRequired,
        });
      }
    }

    if (validationType === 'array') {
      if (property?.items && property?.items?.type === 'object') {
        const itemsValidationRules = getValidationsFromSchema(property.items);
        validationRule = validationRule.of(itemsValidationRules);
      }

      if (property?.minItems !== undefined && property?.minItems !== '') {
        validationRule = validationRule.minItems(parseInt(property?.minItems, 10));
      }

      if (property?.maxItems !== undefined && property?.maxItems !== '') {
        validationRule = validationRule.maxItems(parseInt(property?.maxItems, 10));
      }
    }

    if (validationType === 'object') {
      if (property?.properties) {
        validationRule = getValidationsFromSchema(property);
      }
    }

    if (isRequired) {
      validationRule = validationRule.nullable().required();
    } else {
      validationRule = validationRule.nullable();
    }

    return { ...acc, [propertyKey]: validationRule };
  }, {});

  return object().shape(validations);
};
